import FormController from "./form_controller"
import Logger from '../lib/logger'

/**
 * Provider Form Controller - Extends form controller with provider-specific functionality
 */
export default class extends FormController {
  static targets = [
    "nameField", "urlField", "statusField", "commentsField", "supportField",
    "uploadProgress", "progressBar", "progressText", "toastContainer",
    "duplicateWarningContainer"
  ]
  static values = {
    ...FormController.values,
    maxSize: Number
  }

  connect() {
    super.connect()
    this.maxSizeValue = this.maxSizeValue || 10 * 1024 * 1024 // 10MB default
    this.entityTypeValue = 'provider'
    this.allowedFileTypesValue = [
      'application/vnd.google-earth.kml+xml',
      'application/vnd.google-earth.kmz+xml',
      'application/octet-stream',
      '.kml', '.kmz'
    ]
  }

  // Check for duplicate provider names as user types
  checkDuplicates(event) {
    if (this.duplicateCheckTimeout) clearTimeout(this.duplicateCheckTimeout)
    
    this.duplicateCheckTimeout = setTimeout(() => {
      const name = this.nameFieldTarget.value
      if (name.length < 2) {
        this.hideDuplicateWarning()
        return
      }

      const excludeId = this.nameFieldTarget.dataset.providerId || ''
      
      this.safeFetch(`/providers/check_duplicates?name=${encodeURIComponent(name)}&exclude_id=${excludeId}`, {
        headers: { 'Accept': 'text/html' }
      })
      .then(response => response.text())
      .then(html => {
        if (html.trim().length > 0) {
          this.duplicateWarningContainerTarget.innerHTML = html
          this.duplicateWarningContainerTarget.classList.remove('d-none')
        } else {
          this.hideDuplicateWarning()
        }
      })
      .catch(error => Logger.error('Error checking duplicates:', error, { controller: 'provider-form' }))
    }, 500)
  }

  hideDuplicateWarning() {
    if (!this.hasDuplicateWarningContainerTarget) return
    this.duplicateWarningContainerTarget.classList.add('d-none')
    this.duplicateWarningContainerTarget.innerHTML = ''
  }

  // File validation - override base method to add provider-specific logic
  validateFile(file) {
    const errors = []
    
    // Check file size
    if (file.size > this.maxSizeValue) {
      errors.push(`File size (${this.formatFileSize(file.size)}) exceeds maximum allowed size (${this.formatFileSize(this.maxSizeValue)})`)
    }
    
    // Check file type for KML/KMZ
    const allowedTypes = [
      'application/vnd.google-earth.kml+xml',
      'application/vnd.google-earth.kmz+xml',
      'application/octet-stream'
    ]
    
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(kml|kmz)$/i)) {
      errors.push('File must be a KML or KMZ file')
    }
    
    if (errors.length > 0) {
      this.showToast(errors.join('<br>'), 'error')
      return false
    }
    
    this.showToast(`File "${file.name}" selected successfully (${this.formatFileSize(file.size)})`, 'success')
    return true
  }

  // Auto-save functionality - override base method to add provider-specific logic
  autoSaveForm() {
    this.saveFormState()
    this.showToast('Form auto-saved', 'info')
  }

  // Form submission - override base method to add provider-specific logic
  handleSubmit(event) {
    if (!this.validateForm()) {
      event.preventDefault()
      return false
    }

    // Clear auto-save state on successful submission
    const storageKey = this.getStorageKey()
    if (storageKey) {
      localStorage.removeItem(storageKey)
    }

    // Handle file upload if present
    if (this.hasFileInputTarget && this.fileInputTarget.files.length > 0) {
      const file = this.fileInputTarget.files[0]
      if (!this.validateFile(file)) {
        event.preventDefault()
        return false
      }
    }

    this.showToast('Submitting form...', 'info')
    return true
  }
}
